import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { billingService } from '../services/billingService';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useResetCountdown } from '../hooks/useResetCountdown';
import { getSubscriptionSummary, getFreeDownloadBadge, formatPlanLabel } from '../utils/subscription';

// ─────────────────────────────────────────────────────────────────────────────
// CONFIGURATION
// ─────────────────────────────────────────────────────────────────────────────
const PACKS = [
  { id: '150', credits: 150, price: 5, pricePerUpscale: '0.33', badge: null },
  { id: '400', credits: 400, price: 10, pricePerUpscale: '0.25', badge: 'popular', badgeType: 'popular' },
  { id: '1000', credits: 1000, price: 20, pricePerUpscale: '0.20', badge: 'best', badgeType: 'value' },
];

const SUBSCRIPTION = {
  monthlyCredits: 200,
  price: 4.99,
  features: [],
};

const CREDIT_COSTS = {
  watermarkRemoval: 1,
  upscale2K: 10,
};

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

const PlanBadge = ({ type, className = '' }) => {
  const { t } = useTranslation('billing');
  const styles = {
    pro: 'bg-gradient-to-r from-amber-500 to-orange-500 text-white',
    free: 'bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-300',
    popular: 'bg-indigo-500 text-white',
    value: 'bg-emerald-500 text-white',
  };
  const labels = { pro: 'MUZO+', free: 'GRATUIT', popular: t('packs.popular'), value: t('packs.best') };
  
  return (
    <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${styles[type]} ${className}`}>
      {labels[type]}
    </span>
  );
};

const FeatureItem = ({ icon, text, highlight = false }) => (
  <div className="flex items-center gap-2.5">
    <div className={`h-7 w-7 rounded-md flex items-center justify-center ${highlight ? 'bg-indigo-500 text-white' : 'bg-slate-100 dark:bg-white/10 text-slate-500 dark:text-slate-400'}`}>
      <FontAwesomeIcon icon={icon} className="text-xs" />
    </div>
    <span className={`text-sm ${highlight ? 'font-medium text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-300'}`}>
      {text}
    </span>
  </div>
);

const CreditPackCard = ({ pack, loading, onSelect }) => {
  const { t } = useTranslation('billing');
  const isLoading = loading === pack.id;
  
  const badgeColors = {
    popular: 'bg-indigo-500 text-white',
    value: 'bg-emerald-500 text-white',
  };
  
  return (
    <div
      className={`
        group relative rounded-lg border p-4 transition-all duration-200 cursor-pointer
        ${pack.badge 
          ? 'border-indigo-500/50 bg-indigo-500/5 dark:bg-indigo-500/10 hover:border-indigo-500' 
          : 'border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/50 hover:border-slate-300 dark:hover:border-white/20'
        }
        hover:shadow-md
      `}
      onClick={() => !isLoading && onSelect(pack.id)}
    >
      {/* Badge */}
      {pack.badge && (
        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
          <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide ${badgeColors[pack.badgeType]}`}>
            {t(`packs.${pack.badge}`)}
          </span>
        </div>
      )}
      
      {/* Content */}
      <div className="space-y-3 pt-1">
        {/* Credits Amount */}
        <div className="text-center">
          <p className="text-3xl font-bold text-slate-900 dark:text-white">
            {pack.credits}
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400">{t('packs.credits', { count: pack.credits })}</p>
        </div>
        
        {/* Price */}
        <div className="text-center">
          <p className="text-xl font-bold text-slate-900 dark:text-white">
            {t('packs.price', { value: pack.price })}
          </p>
          <p className="text-[10px] text-slate-400 dark:text-slate-500">
            {t('packs.pricePer', { value: pack.pricePerUpscale })}
          </p>
        </div>
        
        {/* CTA Button */}
        <button
          type="button"
          disabled={isLoading}
          className={`
            w-full py-2.5 rounded-md font-semibold text-sm transition-all duration-200
            ${pack.badge
              ? 'bg-indigo-600 text-white hover:bg-indigo-500'
              : 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-100'
            }
            ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}
          `}
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <FontAwesomeIcon icon="spinner" spin />
              {t('status.redirect')}
            </span>
          ) : (
            t('packs.ctaBuy')
          )}
        </button>
      </div>
    </div>
  );
};

const CurrentPlanCard = ({ subscriptionSummary, credits, freeDownloadBadge, onManageSubscription }) => {
  const { t } = useTranslation('billing');
  const isPro = subscriptionSummary.planType === 'pro';
  const currentPlanLabel = isPro ? 'Muzo+' : formatPlanLabel(subscriptionSummary.planLabel);
  
  return (
    <div className={`
      rounded-lg border overflow-hidden
      ${isPro 
        ? 'border-amber-300/50 dark:border-amber-500/30 bg-gradient-to-br from-amber-50 via-orange-50/50 to-transparent dark:from-amber-500/10 dark:via-orange-500/5 dark:to-transparent' 
        : 'border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/70'
      }
    `}>
      {/* Header */}
      <div className="p-4 pb-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center text-base ${isPro ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-sm' : 'bg-slate-100 dark:bg-white/10 text-slate-400'}`}>
              <FontAwesomeIcon icon={isPro ? 'crown' : 'user'} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                  {currentPlanLabel}
                </h3>
                <PlanBadge type={isPro ? 'pro' : 'free'} />
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {isPro ? t('subscription.active') : t('subscription.freePlan')}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Credits Display */}
      <div className="px-4 pb-4">
        <div className="rounded-md bg-white/80 dark:bg-slate-900/50 border border-slate-200/50 dark:border-white/5 p-3 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider font-medium mb-0.5">
                Solde actuel
              </p>
              <div className="flex items-baseline gap-1.5">
                <span className={`text-2xl font-bold ${(credits ?? 0) <= 0 ? 'text-rose-500' : 'text-slate-900 dark:text-white'}`}>
                  {credits ?? 0}
                </span>
                <span className="text-slate-400 text-xs">crédits</span>
              </div>
            </div>

            <div className="flex flex-col items-end gap-2">
              {freeDownloadBadge && (
                <div className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-[11px] font-semibold border ${
                  freeDownloadBadge.tone === 'success' 
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-500/10 dark:border-emerald-500/30 dark:text-emerald-200' 
                    : freeDownloadBadge.tone === 'info'
                    ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-500/10 dark:border-blue-500/30 dark:text-blue-200'
                    : 'bg-slate-100 border-slate-200 text-slate-600 dark:bg-white/5 dark:border-white/10 dark:text-slate-300'
                }`}>
                  <FontAwesomeIcon 
                    icon={freeDownloadBadge.tone === 'success' ? 'check-circle' : freeDownloadBadge.tone === 'info' ? 'infinity' : 'clock'} 
                    className="text-[10px]"
                  />
                  <span>{freeDownloadBadge.text}</span>
                </div>
              )}
              {isPro && onManageSubscription && (
                <button
                  type="button"
                  onClick={onManageSubscription}
                  className="text-[11px] font-semibold text-amber-700 dark:text-amber-300 hover:text-amber-600 dark:hover:text-amber-200"
                >
                  {t('subscription.manage')}
                </button>
              )}
            </div>
          </div>

          {isPro && (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-[11px] font-semibold bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-white">
                <FontAwesomeIcon icon="infinity" />
                {t('subscription.features.noWatermark')}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-[11px] font-semibold bg-indigo-100 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-200">
                <FontAwesomeIcon icon="bolt" />
                {t('subscription.features.priority')}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const RechargeCredits = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('billing');
  const { credits, userProfile, refreshUserProfile, requireBillingInfo } = useAuth();
  const toast = useToast();
  
  const [loadingPack, setLoadingPack] = useState('');
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  const { explicitFormat: countdownExplicit } = useResetCountdown();

  useEffect(() => {
    refreshUserProfile?.();
  }, [refreshUserProfile]);

  const subscriptionSummary = useMemo(() => getSubscriptionSummary(userProfile), [userProfile]);
  const subscriptionFeatures = useMemo(() => ([
    { icon: 'coins', text: t('subscription.features.credits'), highlight: true },
    { icon: 'infinity', text: t('subscription.features.noWatermark') },
    { icon: 'bolt', text: t('subscription.features.priority') },
    { icon: 'arrow-rotate-right', text: t('subscription.features.autoRenew') },
  ]), [t]);
  
  const freeDownloadBadge = useMemo(() => {
    const badge = getFreeDownloadBadge(subscriptionSummary);
    if (badge && badge.tone === 'warning') {
      return {
        ...badge,
        text: t('status.loading'),
        countdown: countdownExplicit,
      };
    }
    return badge;
  }, [subscriptionSummary, countdownExplicit, t]);

  const isSubscriber = subscriptionSummary.isActive && subscriptionSummary.planType === 'pro';

  // ─────────────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  const handlePackCheckout = async (packId) => {
    // First, require billing info if not complete
    const billingComplete = await requireBillingInfo();
    if (!billingComplete) {
      return; // User cancelled or billing modal was closed without completing
    }
    
    setLoadingPack(packId);
    try {
      const res = await billingService.startCreditPackCheckout(packId);
      const url = res?.checkout_url || res?.url;
      if (url) {
        window.location.href = url;
      } else {
        toast.error(t('status.purchaseError'));
      }
    } catch (err) {
      console.error('Pack checkout error:', err);
      toast.error(err?.response?.data?.detail || t('status.purchaseError'));
    } finally {
      setLoadingPack('');
    }
  };

  const handleSubscription = async () => {
    // First, require billing info if not complete
    const billingComplete = await requireBillingInfo();
    if (!billingComplete) {
      return; // User cancelled or billing modal was closed without completing
    }
    
    setSubscriptionLoading(true);
    try {
      const res = await billingService.startSubscriptionCheckout();
      const url = res?.checkout_url || res?.url;
      if (url) {
        window.location.href = url;
      } else {
        toast.error(t('status.purchaseError'));
      }
    } catch (err) {
      console.error('Subscription checkout error:', err);
      toast.error(err?.response?.data?.detail || t('status.purchaseError'));
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleManageSubscription = () => {
    navigate('/settings?tab=subscription');
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 dark:from-slate-950 dark:via-slate-900 dark:to-indigo-950/30">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        
        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-6 w-6 rounded-md bg-indigo-500 flex items-center justify-center">
              <FontAwesomeIcon icon="coins" className="text-white text-[10px]" />
            </div>
            <p className="text-[10px] uppercase tracking-[0.15em] text-indigo-500 font-bold">
              {t('title')}
            </p>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2">
            {t('title')}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xl">
            {t('subtitle')}
          </p>
        </header>

        {/* Main Grid */}
        <div className={`grid gap-4 mb-8 ${isSubscriber ? '' : 'lg:grid-cols-5'}`}>
          
          {/* Current Plan Card */}
          <div className={isSubscriber ? '' : 'lg:col-span-2'}>
            <CurrentPlanCard 
              subscriptionSummary={subscriptionSummary}
              credits={credits}
              freeDownloadBadge={freeDownloadBadge}
              onManageSubscription={isSubscriber ? handleManageSubscription : null}
            />
          </div>

          {/* Subscription Card - Only show full card for non-subscribers */}
          {!isSubscriber && (
            <div className="lg:col-span-3">
              <div className="h-full rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/70 overflow-hidden">
                {/* Header */}
                <div className="p-4 border-b border-slate-100 dark:border-white/5">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-base shadow-sm">
                        <FontAwesomeIcon icon="crown" />
                      </div>
                      <div>
                        <h2 className="text-base font-bold text-slate-900 dark:text-white">Muzo+</h2>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{t('subscription.title')}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-slate-900 dark:text-white">
                        {t('subscription.month', { value: SUBSCRIPTION.price.toFixed(2) })}
                        <span className="text-sm font-normal text-slate-400"> /mois</span>
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Features */}
                <div className="p-4">
                  <div className="grid sm:grid-cols-2 gap-3 mb-4">
                    {subscriptionFeatures.map((feature, idx) => (
                      <FeatureItem key={idx} {...feature} />
                    ))}
                  </div>
                  
                  {/* CTA */}
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={handleSubscription}
                      disabled={subscriptionLoading}
                      className={`
                        px-5 py-2.5 rounded-md font-semibold text-sm transition-all duration-200 flex items-center gap-2
                        bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-400 hover:to-orange-400 shadow-sm
                        ${subscriptionLoading ? 'opacity-70 cursor-not-allowed' : ''}
                      `}
                    >
                      {subscriptionLoading ? (
                        <>
                          <FontAwesomeIcon icon="spinner" spin />
                          {t('status.loading')}
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon="crown" />
                          {t('subscription.cta')}
                        </>
                      )}
                    </button>
                    <p className="text-[10px] text-slate-400 dark:text-slate-500">
                      <FontAwesomeIcon icon="shield-halved" className="mr-1" />
                      {t('subscription.secure')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Credit Packs Section */}
        <section className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                {t('packs.sectionTitle')}
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {t('packs.sectionSubtitle')}
              </p>
            </div>
            <div className="hidden sm:flex items-center gap-1.5 text-[10px] text-slate-400">
              <FontAwesomeIcon icon="bolt" />
              <span>{t('packs.instant')}</span>
            </div>
          </div>
          
          <div className="grid sm:grid-cols-3 gap-3">
            {PACKS.map((pack) => (
              <CreditPackCard
                key={pack.id}
                pack={pack}
                loading={loadingPack}
                onSelect={handlePackCheckout}
              />
            ))}
          </div>
        </section>

        {/* Pricing Info */}
        <section className="rounded-lg border border-slate-200 dark:border-white/10 bg-white/50 dark:bg-slate-900/30 p-4">
          <h3 className="text-xs font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-1.5">
            <FontAwesomeIcon icon="info-circle" className="text-indigo-500 text-[10px]" />
            {t('info.how')}
          </h3>
          
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="flex items-start gap-2.5">
              <div className="h-8 w-8 rounded-md bg-emerald-100 dark:bg-emerald-500/10 flex items-center justify-center text-emerald-600 dark:text-emerald-400 flex-shrink-0">
                <FontAwesomeIcon icon="download" className="text-xs" />
              </div>
              <div>
                <p className="font-semibold text-slate-900 dark:text-white text-sm">{t('info.free')}</p>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                  {t('info.freeSubtitle')}
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-2.5">
              <div className="h-8 w-8 rounded-md bg-indigo-100 dark:bg-indigo-500/10 flex items-center justify-center text-indigo-600 dark:text-indigo-400 flex-shrink-0">
                <FontAwesomeIcon icon="eraser" className="text-xs" />
              </div>
              <div>
                <p className="font-semibold text-slate-900 dark:text-white text-sm">{t('info.watermarkCost', { value: CREDIT_COSTS.watermarkRemoval })}</p>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                  {t('info.watermarkSubtitle')}
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-2.5">
              <div className="h-8 w-8 rounded-md bg-violet-100 dark:bg-violet-500/10 flex items-center justify-center text-violet-600 dark:text-violet-400 flex-shrink-0">
                <FontAwesomeIcon icon="expand" className="text-xs" />
              </div>
              <div>
                <p className="font-semibold text-slate-900 dark:text-white text-sm">{t('info.upscaleCost', { value: CREDIT_COSTS.upscale2K })}</p>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                  {t('info.upscaleSubtitle')}
                </p>
              </div>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
};

export default RechargeCredits;
